import React, { useEffect, useMemo, useState } from 'react';
import { Lightbulb, Sparkles, Target } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/primitives/Dialog';
import { cn } from '../../../lib/cn';
import { UNIT_SESSION_COUNT_OPTIONS } from '../../constants/coursePlanPresets';
import type { CourseProfile } from '../../types/courseProfile';
import { GuideCurriculumPanel } from '../GuideCurriculumPanel';
import { BriefEditorPanel, isBriefReadyForGeneration } from './BriefEditorPanel';
import { analyzeBriefCompleteness } from '../../lib/analyzeBriefCompleteness';
import {
  generateUnitBriefWithAi,
  mapGenerateUnitBriefError,
} from '../../api/planApi';
import type { CurriculumTarget, GuideGenerationMode } from '@interactive-room';
import {
  curriculumUnavailableMessage,
  hasCurriculumSubjectSupport,
} from '../../constants/subjectsWithCurriculum';

const WIZARD_LABEL =
  'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500';
const WIZARD_INPUT =
  'w-full rounded-lg border border-transparent bg-gray-50 px-3 py-2.5 text-sm text-gray-900 transition placeholder:text-gray-400 focus:border-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-900';

type EntryMode = 'select' | 'libre' | 'curricular';
type LibreStep = 1 | 2;
type CurricularStep = 1 | 2 | 3;

export interface UnitPlanWizardPayload {
  brief: string;
  title: string;
  sessionCount: number;
  generationMode: GuideGenerationMode;
  curriculumTarget: CurriculumTarget | null;
  entryMode: 'libre' | 'curricular';
}

interface UnitPlanWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  course: CourseProfile;
  isGenerating: boolean;
  error: string | null;
  onGenerate: (payload: UnitPlanWizardPayload) => void;
}

function StepProgress({
  entryMode,
  libreStep,
  curricularStep,
}: {
  entryMode: EntryMode;
  libreStep: LibreStep;
  curricularStep: CurricularStep;
}) {
  if (entryMode === 'select') return null;

  const label =
    entryMode === 'libre'
      ? libreStep === 1
        ? 'La Idea'
        : 'Marco Curricular'
      : curricularStep === 1
        ? 'Tablero Curricular'
        : curricularStep === 2
          ? 'Brief Técnico'
          : 'Generar Ruta';

  const total = entryMode === 'libre' ? 2 : 3;
  const current = entryMode === 'libre' ? libreStep : curricularStep;

  return (
    <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
      Paso {current} de {total}: {label}
    </p>
  );
}

function ModeCard({
  icon,
  title,
  description,
  onClick,
  disabled,
  titleAttr,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  titleAttr?: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex flex-col items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-5 text-left transition',
        'hover:border-gray-200 hover:bg-white hover:shadow-sm',
        'focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2',
        disabled && 'cursor-not-allowed opacity-50',
      )}
      onClick={onClick}
      disabled={disabled}
      title={titleAttr}
      aria-disabled={disabled || undefined}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-900 shadow-sm">
        {icon}
      </span>
      <span>
        <span className="block text-sm font-semibold text-gray-900">{title}</span>
        <span className="mt-1 block text-xs leading-relaxed text-gray-500">{description}</span>
      </span>
    </button>
  );
}

export function UnitPlanWizardDialog({
  open,
  onOpenChange,
  course,
  isGenerating,
  error,
  onGenerate,
}: UnitPlanWizardDialogProps) {
  const [entryMode, setEntryMode] = useState<EntryMode>('select');
  const [libreStep, setLibreStep] = useState<LibreStep>(1);
  const [curricularStep, setCurricularStep] = useState<CurricularStep>(1);
  const [brief, setBrief] = useState('');
  const [title, setTitle] = useState('');
  const [sessionCount, setSessionCount] = useState<number>(6);
  const [generationMode, setGenerationMode] = useState<GuideGenerationMode>('curricular');
  const [curriculumTarget, setCurriculumTarget] = useState<CurriculumTarget | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);

  const hasCurriculumSupport = hasCurriculumSubjectSupport(course.subjectTag);
  const briefAnalysis = useMemo(() => analyzeBriefCompleteness(brief), [brief]);

  const curricularReady = Boolean(
    curriculumTarget?.axis &&
      curriculumTarget?.oaCode &&
      curriculumTarget?.oahCode &&
      curriculumTarget?.oaaCode,
  );

  const libreCurricularReady =
    !hasCurriculumSupport ||
    generationMode === 'independent' ||
    curricularReady;

  const resetWizard = () => {
    setEntryMode('select');
    setLibreStep(1);
    setCurricularStep(1);
    setBrief('');
    setTitle('');
    setSessionCount(6);
    setGenerationMode('curricular');
    setCurriculumTarget(null);
    setStepError(null);
    setIsGeneratingBrief(false);
  };

  useEffect(() => {
    if (!open) return;
    resetWizard();
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setStepError(null);
    }
    onOpenChange(next);
  };

  const goBack = () => {
    setStepError(null);
    if (entryMode === 'libre') {
      if (libreStep === 2) setLibreStep(1);
      else setEntryMode('select');
      return;
    }
    if (entryMode === 'curricular') {
      if (curricularStep === 3) setCurricularStep(2);
      else if (curricularStep === 2) setCurricularStep(1);
      else setEntryMode('select');
    }
  };

  const handleGenerateCurricularBrief = async () => {
    if (!curricularReady || !curriculumTarget) {
      setStepError('Selecciona eje, OA, OAH y OAA para continuar.');
      return;
    }

    setIsGeneratingBrief(true);
    setStepError(null);

    try {
      const result = await generateUnitBriefWithAi({
        curriculumTarget,
        sessionCount,
        courseProfile: course,
      });
      setBrief(result.brief);
      if (result.suggestedTitle && !title.trim()) {
        setTitle(result.suggestedTitle);
      }
      setCurricularStep(2);
    } catch (err) {
      const code = err instanceof Error ? err.message : 'generate_unit_brief_failed';
      setStepError(mapGenerateUnitBriefError(code));
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  const handleLibreNext = () => {
    const trimmedBrief = brief.trim();
    if (trimmedBrief.length < 8) {
      setStepError('Escribe un brief más detallado (mínimo 8 caracteres).');
      return;
    }
    setStepError(null);
    if (!hasCurriculumSupport) {
      onGenerate({
        brief: trimmedBrief,
        title: title.trim(),
        sessionCount,
        generationMode: 'independent',
        curriculumTarget: null,
        entryMode: 'libre',
      });
      return;
    }
    setLibreStep(2);
  };

  const handleCurricularBriefNext = () => {
    if (!isBriefReadyForGeneration(briefAnalysis)) {
      setStepError('Completa el esqueleto del brief antes de continuar.');
      return;
    }
    setStepError(null);
    setCurricularStep(3);
  };

  const handleGenerate = () => {
    const trimmedBrief = brief.trim();
    if (trimmedBrief.length < 8) {
      setStepError('Escribe un brief más detallado (mínimo 8 caracteres).');
      return;
    }

    const mode = entryMode === 'curricular' ? 'curricular' : generationMode;
    const target =
      entryMode === 'curricular'
        ? curriculumTarget
        : generationMode === 'curricular'
          ? curriculumTarget
          : null;

    if (mode === 'curricular' && !curricularReady) {
      setStepError('Selecciona eje, OA, OAH y OAA para planificación curricular.');
      return;
    }

    setStepError(null);
    onGenerate({
      brief: trimmedBrief,
      title: title.trim(),
      sessionCount,
      generationMode: mode,
      curriculumTarget: target,
      entryMode: entryMode === 'curricular' ? 'curricular' : 'libre',
    });
  };

  const displayError = stepError ?? error;
  const busy = isGenerating || isGeneratingBrief;

  const dialogDescription = (() => {
    if (entryMode === 'select') {
      return `Elige cómo quieres planificar la unidad para ${course.displayName}.`;
    }
    if (entryMode === 'curricular') {
      if (curricularStep === 1) {
        return 'Selecciona los objetivos MINEDUC. La IA propondrá un brief técnico que conecte OA, OAH y OAA.';
      }
      if (curricularStep === 2) {
        return 'Revisa y ajusta el esqueleto técnico antes de generar la ruta de aprendizaje.';
      }
      return 'Confirma título y cantidad de hitos. La ruta se alineará al marco curricular elegido.';
    }
    if (libreStep === 1) {
      return `Describe la intención pedagógica. La IA propondrá hitos con foco y evidencia para ${course.displayName}.`;
    }
    return 'Alinea la ruta con el marco tridimensional MINEDUC (OA, OAH, OAA) o elige planificación libre.';
  })();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-0 bg-white p-8 shadow-2xl">
        <DialogHeader className="space-y-3 text-left">
          <StepProgress
            entryMode={entryMode}
            libreStep={libreStep}
            curricularStep={curricularStep}
          />
          <DialogTitle className="text-xl font-semibold tracking-tight text-gray-900">
            {entryMode === 'select' ? 'Nueva unidad' : 'Generar ruta de aprendizaje'}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-gray-500">
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>

        {displayError ? (
          <p className="mt-4 text-sm text-red-500" role="alert">
            {displayError}
          </p>
        ) : null}

        {entryMode === 'select' ? (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <ModeCard
              icon={<Target className="h-5 w-5" aria-hidden />}
              title="Modo Curricular"
              description={
                hasCurriculumSupport
                  ? 'Tengo OAs que cubrir. Diseño inverso: currículum → brief → ruta.'
                  : curriculumUnavailableMessage(course.subjectTag)
              }
              onClick={() => {
                if (!hasCurriculumSupport) {
                  setStepError(curriculumUnavailableMessage(course.subjectTag));
                  return;
                }
                setStepError(null);
                setEntryMode('curricular');
                setGenerationMode('curricular');
              }}
              disabled={!hasCurriculumSupport}
              titleAttr={
                hasCurriculumSupport
                  ? undefined
                  : curriculumUnavailableMessage(course.subjectTag)
              }
            />
            <ModeCard
              icon={<Lightbulb className="h-5 w-5" aria-hidden />}
              title="Modo Libre"
              description="Tengo una idea o proyecto. Parto de mi brief pedagógico."
              onClick={() => {
                setStepError(null);
                setEntryMode('libre');
              }}
            />
          </div>
        ) : null}

        {entryMode === 'curricular' && curricularStep === 1 ? (
          <div className="mt-6 space-y-6">
            <GuideCurriculumPanel
              variant="planning"
              appearance="wizard"
              level={course.level}
              subjectTag={course.subjectTag}
              generationMode="curricular"
              curriculumTarget={curriculumTarget}
              onGenerationModeChange={setGenerationMode}
              onCurriculumTargetChange={setCurriculumTarget}
              hideModeToggle
              lockCurricular
            />

            <label className="block">
              <span className={WIZARD_LABEL}>Cantidad de hitos previstos</span>
              <select
                className={cn(WIZARD_INPUT, 'cursor-pointer truncate')}
                value={sessionCount}
                onChange={(event) => setSessionCount(Number(event.target.value))}
              >
                {UNIT_SESSION_COUNT_OPTIONS.map((count) => (
                  <option key={count} value={count}>
                    {count} hitos
                  </option>
                ))}
              </select>
            </label>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                onClick={goBack}
                disabled={busy}
              >
                ← Volver
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => void handleGenerateCurricularBrief()}
                disabled={busy || !curricularReady}
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                {isGeneratingBrief ? 'Generando brief…' : 'Generar brief técnico →'}
              </button>
            </div>
          </div>
        ) : null}

        {entryMode === 'curricular' && curricularStep === 2 ? (
          <div className="mt-6 space-y-6">
            <BriefEditorPanel
              value={brief}
              onChange={setBrief}
              isLoading={isGeneratingBrief}
              helperText="Edita el esqueleto técnico. La barra verde indica que el brief está listo para generar la ruta."
            />

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                onClick={goBack}
                disabled={busy}
              >
                ← Volver
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleCurricularBriefNext}
                disabled={busy || !isBriefReadyForGeneration(briefAnalysis)}
              >
                Siguiente: Confirmar ruta →
              </button>
            </div>
          </div>
        ) : null}

        {entryMode === 'curricular' && curricularStep === 3 ? (
          <div className="mt-6 space-y-5">
            <label className="block">
              <span className={WIZARD_LABEL}>Título de la unidad</span>
              <input
                type="text"
                className={WIZARD_INPUT}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Ej: Unidad 2 — Fracciones equivalentes"
              />
            </label>

            <label className="block">
              <span className={WIZARD_LABEL}>Cantidad de hitos en la ruta</span>
              <select
                className={cn(WIZARD_INPUT, 'cursor-pointer truncate')}
                value={sessionCount}
                onChange={(event) => setSessionCount(Number(event.target.value))}
              >
                {UNIT_SESSION_COUNT_OPTIONS.map((count) => (
                  <option key={count} value={count}>
                    {count} hitos
                  </option>
                ))}
              </select>
            </label>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                onClick={goBack}
                disabled={busy}
              >
                ← Volver
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleGenerate}
                disabled={busy}
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                {isGenerating ? 'Generando…' : 'Generar Ruta y Hitos'}
              </button>
            </div>
          </div>
        ) : null}

        {entryMode === 'libre' && libreStep === 1 ? (
          <div className="mt-6 space-y-5">
            <label className="block">
              <span className={WIZARD_LABEL}>Brief / foco de la unidad</span>
              <textarea
                className={cn(WIZARD_INPUT, 'min-h-[128px] resize-y')}
                value={brief}
                onChange={(event) => setBrief(event.target.value)}
                placeholder="Ej: Fracciones equivalentes con material concreto y problemas de reparto en contexto escolar…"
              />
            </label>

            <label className="block">
              <span className={WIZARD_LABEL}>Título sugerido (opcional)</span>
              <input
                type="text"
                className={WIZARD_INPUT}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Ej: Unidad 2 — Fracciones"
              />
            </label>

            <label className="block">
              <span className={WIZARD_LABEL}>Cantidad de hitos en la ruta</span>
              <select
                className={cn(WIZARD_INPUT, 'cursor-pointer truncate')}
                value={sessionCount}
                onChange={(event) => setSessionCount(Number(event.target.value))}
              >
                {UNIT_SESSION_COUNT_OPTIONS.map((count) => (
                  <option key={count} value={count}>
                    {count} hitos
                  </option>
                ))}
              </select>
            </label>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                onClick={goBack}
                disabled={busy}
              >
                ← Volver
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleLibreNext}
                disabled={busy}
              >
                {hasCurriculumSupport ? (
                  'Siguiente: Alineación Curricular →'
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" aria-hidden />
                    {isGenerating ? 'Generando…' : 'Generar Ruta y Hitos'}
                  </>
                )}
              </button>
            </div>
          </div>
        ) : null}

        {entryMode === 'libre' && libreStep === 2 ? (
          <div className="mt-6 space-y-6">
            <GuideCurriculumPanel
              variant="planning"
              appearance="wizard"
              level={course.level}
              subjectTag={course.subjectTag}
              generationMode={generationMode}
              curriculumTarget={curriculumTarget}
              onGenerationModeChange={setGenerationMode}
              onCurriculumTargetChange={setCurriculumTarget}
            />

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
                onClick={goBack}
                disabled={busy}
              >
                ← Volver
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleGenerate}
                disabled={busy || !libreCurricularReady}
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                {isGenerating ? 'Generando…' : 'Generar Ruta y Hitos'}
              </button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
